import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";

// constants
import { NOTES_CHECKBOX_OPTIONS } from "@/props-constants/dashboard-props";

// material
import { Box, makeStyles, CircularProgress, Button, withStyles } from "@material-ui/core";

// components
import InputBox from "@/components/shared/InputBox";
import Preview from "./preview";

// actions
import { uploadStudentNote } from "@/store/actions/notesActions";

const useStyles = makeStyles((theme) => ({
    detailHeading: {
        color: theme.palette.text.disabled,
        marginBottom: theme.spacing(0.5),
    },
    inputBoxContainer: {
        width: "100%",
        margin: theme.spacing(0, 0, 1),
    },
}));

const AddNoteButton = withStyles((theme) => ({
    root: {
        color: theme.palette.success.main,
        borderColor: theme.palette.success.main,
        "&:hover": {
            color: theme.palette.common.white,
            background: "#00C853",
            borderColor: "#00C853",
        },
    },
}))(Button);

export default function Notes({ changeTab, registrantCode }) {
    const classes = useStyles();
    const dispatch = useDispatch();

    const data = useSelector((state) => state.notes.data);
    const loading = useSelector((state) => state.notes.loading);
    const universityDetails = useSelector((state) => state.universityDetails.data);

    const initOptionsInNote = React.useMemo(() =>
        NOTES_CHECKBOX_OPTIONS.ENUMS.reduce((acc, option) => {
            const name = option.toLowerCase().replace(/ /g, "-");
            return {
                ...acc,
                [name]: false,
            };
        }, {})
    );
    const [optionInNote, setOptionInNote] = React.useState({ ...initOptionsInNote });
    const [note, setNote] = useState("");

    const handleInputBoxChange = (event) => {
        setNote(event.target.value);
    };
    const handleCheckboxBoxChange = (values) => {
        let updatedNote = note,
            updatedOptionInNote = optionInNote;

        Object.values(values).forEach(({ name, value, selected }) => {
            const valueExistInNote = updatedOptionInNote[name];

            if (!selected && valueExistInNote) {
                const valuePos = updatedNote.indexOf(value);
                let valueToRemove = value;
                if (updatedNote[valuePos - 2] === "|") valueToRemove = ` | ${value}`;
                updatedNote = updatedNote.replace(valueToRemove, "");
            } else if (selected && !valueExistInNote) {
                updatedNote += updatedNote.length > 0 ? ` | ${value}` : value;
            }

            updatedOptionInNote[name] = selected;
        });

        setNote(updatedNote);
        setOptionInNote({ ...updatedOptionInNote });
    };

    const handleSubmitNoteRequest = () => {
        if (note.trim()) {
            if (universityDetails.universityName) {
                const universityAppendedNote = `${note.trim()} | ${universityDetails.universityName}`;
                const payload = {
                    notes: universityAppendedNote,
                    university_code: universityDetails.universityCode,
                    fair_id: universityDetails.fairID,
                };
                dispatch(uploadStudentNote(registrantCode, payload, changeTab));
                setNote("");
                setOptionInNote({ ...initOptionsInNote });
            }
        }
    };

    return (
        <Box>
            <Preview notes={data} />
            <InputBox
                classes={{ cInputBoxContainer: classes.inputBoxContainer }}
                multiline={true}
                onChange={handleInputBoxChange}
                onCheck={handleCheckboxBoxChange}
                defaultValue={note}
                rows={10}
                showCheckBoxOptions={true}
                checkBoxOptions={NOTES_CHECKBOX_OPTIONS.ENUMS}
                value={note}
                disabled={loading}
                clipboardBarLabel="Add notes"
            />
            <AddNoteButton variant="outlined" fullWidth={true} onClick={handleSubmitNoteRequest}>
                {loading ? <CircularProgress color={"primary"} size={30} /> : "Add Note"}
            </AddNoteButton>
        </Box>
    );
}

Notes.propTypes = {
    registrantCode: PropTypes.string,
    changeTab: PropTypes.func.isRequired,
};
